<mat-card class="trinta-card admin-images-card mb-25 bg-white border-none d-block">
  <mat-card-header>
    <mat-card-title>
      <h5 class="mt-0 mb-5">Product Image Library</h5>
      <p class="subtitle">Browse uploaded product images, filter by brand or item, and download assets as needed.</p>
    </mat-card-title>
  </mat-card-header>
  <mat-card-content style="overflow: visible !important; max-height: none !important;">
    <form class="filters-form" [formGroup]="filtersForm" (ngSubmit)="applyFilters()">
      <div class="filters-grid">
        <mat-form-field class="compact-field" appearance="outline">
          <mat-label>Brand</mat-label>
          <input matInput formControlName="brand">
        </mat-form-field>

        <mat-form-field class="compact-field" appearance="outline">
          <mat-label>Item</mat-label>
          <input matInput formControlName="item">
        </mat-form-field>

        <div class="filters-actions">
          <button mat-flat-button color="primary" type="submit">Apply</button>
          <button mat-stroked-button type="button" (click)="resetFilters()">Reset</button>
        </div>
      </div>
    </form>

    <div *ngIf="errorMessage" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div class="table-toolbar" *ngIf="!isInitialLoad">
      <div class="results-info" *ngIf="totalImages > 0">
        Showing
        <span class="results-highlight">{{ images.length }}</span>
        of
        <span class="results-highlight">{{ totalImages }}</span>
        images
      </div>
    </div>

    <div class="table-container" [class.is-loading]="isLoading">
      <div class="loading-overlay" *ngIf="isLoading">
        <mat-spinner diameter="44" strokeWidth="4" color="accent"></mat-spinner>
        <span>Loading images...</span>
      </div>

      <div
        class="table-wrapper"
        *ngIf="images.length > 0"
        #tableWrapper
        (wheel)="onTableWheel($event)"
        (touchstart)="onTableTouchStart($event)"
        (touchmove)="onTableTouchMove($event)"
      >
        <table mat-table [dataSource]="images" class="images-table">
          <ng-container matColumnDef="image_name">
            <th mat-header-cell *matHeaderCellDef>Image ID</th>
            <td mat-cell *matCellDef="let image">
              <div
                class="cell-preview-trigger"
                [class.cell-preview-loading]="isPreviewing(image.id)"
                [matTooltip]="isPreviewing(image.id) ? 'Loading preview…' : 'Click to preview image'"
                matTooltipShowDelay="300"
                (click)="previewImage(image)"
              >
                <div class="cell-primary">
                  {{ image.id }}
                  <mat-spinner *ngIf="isPreviewing(image.id)" diameter="12" strokeWidth="2" class="inline-spinner"></mat-spinner>
                </div>
                <div class="cell-secondary" *ngIf="image.image_name">
                  {{ image.image_name?.split('/')?.pop() }}
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="brand">
            <th mat-header-cell *matHeaderCellDef>Brand</th>
            <td mat-cell *matCellDef="let image">{{ image.brand || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="item">
            <th mat-header-cell *matHeaderCellDef>Item</th>
            <td mat-cell *matCellDef="let image">{{ image.item || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="material">
            <th mat-header-cell *matHeaderCellDef>Material</th>
            <td mat-cell *matCellDef="let image">{{ image.material || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="color">
            <th mat-header-cell *matHeaderCellDef>Color</th>
            <td mat-cell *matCellDef="let image">{{ image.color || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="product_age">
            <th mat-header-cell *matHeaderCellDef>Age</th>
            <td mat-cell *matCellDef="let image">{{ image.product_age || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="product_condition">
            <th mat-header-cell *matHeaderCellDef>Condition</th>
            <td mat-cell *matCellDef="let image">{{ image.product_condition || '—' }}</td>
          </ng-container>

          <ng-container matColumnDef="price">
            <th mat-header-cell *matHeaderCellDef>Price</th>
            <td mat-cell *matCellDef="let image">
              {{ image.price !== null && image.price !== undefined ? (image.price | currency:'USD':'symbol':'1.0-2') : '—' }}
            </td>
          </ng-container>

          <ng-container matColumnDef="edit">
            <th mat-header-cell *matHeaderCellDef class="actions-column">Edit</th>
            <td mat-cell *matCellDef="let image" class="actions-column">
              <button
                mat-stroked-button
                color="primary"
                (click)="openEditDialog(image)"
                matTooltip="Edit product details"
                class="icon-only-button edit-button"
              >
                <i-feather name="edit" class="edit-button-icon"></i-feather>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="actions-column">Download</th>
            <td mat-cell *matCellDef="let image" class="actions-column">
              <button
                mat-stroked-button
                color="primary"
                (click)="downloadImage(image)"
                [disabled]="isDownloading(image.id)"
                matTooltip="Download image"
                class="icon-only-button"
              >
                <mat-progress-spinner
                  *ngIf="isDownloading(image.id)"
                  diameter="20"
                  mode="indeterminate"
                ></mat-progress-spinner>
                <img 
                  *ngIf="!isDownloading(image.id)" 
                  src="assets/feather_i/download-cloud.svg" 
                  alt="Download" 
                  class="download-button-icon"
                >
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="delete">
            <th mat-header-cell *matHeaderCellDef class="actions-column delete-column">Delete</th>
            <td mat-cell *matCellDef="let image" class="actions-column delete-column">
              <button
                mat-stroked-button
                color="warn"
                (click)="openDeleteDialog(image)"
                [disabled]="isDeleting(image.id) || isLoadingDeletePreview(image.id)"
                matTooltip="Delete image"
                class="icon-only-button delete-button"
              >
                <mat-progress-spinner
                  *ngIf="isDeleting(image.id) || isLoadingDeletePreview(image.id)"
                  diameter="20"
                  mode="indeterminate"
                ></mat-progress-spinner>
                <i-feather
                  *ngIf="!isDeleting(image.id) && !isLoadingDeletePreview(image.id)"
                  name="trash-2"
                  class="delete-button-icon"
                ></i-feather>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>

        <mat-paginator
          [length]="totalImages"
          [pageSize]="pageSize"
          [pageSizeOptions]="pageSizeOptions"
          [pageIndex]="pageIndex"
          (page)="handlePageEvent($event)"
        ></mat-paginator>
      </div>

      <div
        class="empty-state"
        *ngIf="!images.length && !isLoading && !errorMessage && !isInitialLoad"
      >
        <i-feather name="image"></i-feather>
        <p>No images match the current filters.</p>
      </div>
    </div>
  </mat-card-content>
</mat-card>
